<script setup lang="ts">
    import { getConfig, klinikenApi } from "@/api";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import KAutocompleteOption from "@/components/forms/inputs/Autocomplete/KAutocompleteOption.vue";
    import { Option } from "@/components/forms/inputs/Autocomplete/types";
    import { defineProps, ref, toRefs, withDefaults } from "vue";
    import { openNLLErrorDialog } from "@/tabs/Receptforskrivning/helpers";

    const emit = defineEmits<{
        (event: "inputValue", payload: { value: Option & { adress: string } }): void;
    }>();

    const props = withDefaults(
        defineProps<{
            formRef?: any;
        }>(),
        {}
    );

    const { formRef } = toRefs(props);

    const autocompleteRef = ref();

    const onSearch = async ({ query }: { query?: string }) => {
        if (!query) {
            return [];
        }

        try {
            const { data } = await klinikenApi.get<{
                results: Array<{
                    EA_LOC_CODE: string;
                    BENAMNING_LANG: string;
                    BESOKSADRESS: string;
                    ORT: string;
                }>;
            }>(
                "apotek/apotek",
                getConfig({
                    params: {
                        q: query,
                    },
                })
            );

            return data.results
                .filter((apotek) => {
                    // Rensar bort apotek som används för testning
                    return !["7350045514400", "7350045514448", "7350045514462", "7350045511119"].includes(
                        apotek.EA_LOC_CODE
                    );
                })
                .map((apotek) => ({
                    value: apotek.EA_LOC_CODE,
                    label: apotek.BENAMNING_LANG.toLowerCase(),
                    adress: `${apotek.BESOKSADRESS.toLowerCase()}, ${apotek.ORT.toLowerCase()}`,
                }));
        } catch (error: any) {
            console.error(error);

            openNLLErrorDialog({ error, description: "Kunde inte söka efter apotek." });

            return [];
        }
    };

    const onValue = ({ value }: { value: unknown }) => {
        const typedValue = value as Option & { adress: string };

        if (formRef?.value && autocompleteRef?.value) {
            (formRef.value as { setFieldValue: (props: { value: Option; name: string }) => void }).setFieldValue({
                value: typedValue,
                name: "apotek",
            });
        }

        emit("inputValue", { value: typedValue });
    };

    const reduce = (option: Option) => {
        return option;
    };
</script>

<template>
    <KAutocomplete
        ref="autocompleteRef"
        name="apotek"
        class="apotek-autocomplete"
        :options="[]"
        :onSearch="onSearch"
        v-bind="$attrs"
        @inputValue="onValue"
        :reduce="reduce"
    >
        <template #option="{ option }">
            <KAutocompleteOption contentDirection="vertical" class="apotek-autocomplete-option">
                <span class="label">{{ option.label }}</span>
                <span class="adress">{{ option.adress }}</span>
            </KAutocompleteOption>
        </template>
    </KAutocomplete>
</template>

<style lang="scss" scoped>
    .apotek-autocomplete {
        ::v-deep .vs__dropdown-menu {
            top: unset;
            bottom: 100%;
        }

        ::v-deep .vs__selected {
            text-transform: capitalize;
        }
    }

    .apotek-autocomplete-option {
        text-transform: capitalize;

        .adress {
            font-size: 0.8rem;
        }
    }
</style>
